import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Clock, Calendar, ArrowRight, Star, Award, TrendingUp, Home, ChevronRight } from 'lucide-react';
import { getAllBlogPosts, BLOG_CATEGORIES } from '../data/blogData';
import CmsGuideList from '../components/CmsGuideList';

const BlogIndex = () => {
  const allBlogs = getAllBlogPosts();
  const year = new Date().getFullYear();

  return (
    <>
      <Helmet>
        <title>{`Construction Tools & Software Blog | Expert Reviews & Comparisons ${year}`}</title>
        <meta name="description" content={`Read expert reviews and comparisons of the best construction tools and software in ${year}. Find top-rated estimating tools, AI solutions, and project management platforms.`} />
        <meta name="keywords" content="construction software blog, construction tools reviews, estimating software comparison, construction technology, best construction tools, AI construction tools" />
        
        {/* Open Graph */}
        <meta property="og:title" content={`Construction Tools Blog - Expert Reviews ${year}`} />
        <meta property="og:description" content="Expert reviews and comparisons of construction tools and software" />
        <meta property="og:type" content="website" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Construction Tools & Software Blog",
            "description": "Expert reviews and comparisons of construction tools",
            "url": "https://costtoconstruct.com/blog",
            "publisher": {
              "@type": "Organization",
              "name": "Cost to Construction"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            {/* Breadcrumb */}
            <nav className="flex items-center text-sm text-blue-100 mb-6">
              <Home className="mr-2" size={16} />
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="mx-2" size={14} />
              <span className="text-white font-medium">Blog</span>
            </nav>

            <div className="text-center">
              <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <BookOpen className="mr-2" size={20} />
                <span className="font-semibold">Construction Tools & Technology</span>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Expert Reviews & Tool Comparisons
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Discover the best construction software, AI tools, and technology solutions. 
                Expert reviews, detailed comparisons, and honest recommendations.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Featured Post */}
          <div className="mb-16">
            <div className="flex items-center mb-8">
              <Award className="mr-3 text-yellow-500" size={32} />
              <h2 className="text-3xl font-bold text-gray-900">Featured Article</h2>
            </div>
            
            {allBlogs.length > 0 && (
              <Link
                to={`/blog/${allBlogs[0].slug}`}
                className="block group"
              >
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-yellow-200">
                  <div className="p-8 lg:p-12">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-bold">
                        FEATURED
                      </span>
                      <span className="bg-white/80 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold">
                        {BLOG_CATEGORIES[allBlogs[0].category]?.icon} {BLOG_CATEGORIES[allBlogs[0].category]?.label}
                      </span>
                    </div>
                    
                    <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                      {allBlogs[0].title}
                    </h3>
                    
                    <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                      {allBlogs[0].introduction.substring(0, 250)}...
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-6">
                      <div className="flex items-center">
                        <Calendar className="mr-2" size={16} />
                        <span>Updated {new Date(allBlogs[0].lastModified).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="mr-2" size={16} />
                        <span>{allBlogs[0].readTime} read</span>
                      </div>
                      <div className="flex items-center">
                        <Star className="mr-2 fill-yellow-400 text-yellow-400" size={16} />
                        <span>{allBlogs[0].type === 'guide' ? 'Expert Guide' : 'Top 10 Rankings'}</span>
                      </div>
                    </div>
                    
                    <div className="inline-flex items-center text-blue-600 font-semibold text-lg group-hover:text-blue-700 transition-colors">
                      Read Full Article
                      <ArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform" size={20} />
                    </div>
                  </div>
                </div>
              </Link>
            )}
          </div>

          {/* All Blog Posts Grid */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">All Articles</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allBlogs.map((blog, index) => (
              <Link
                key={blog.slug}
                to={`/blog/${blog.slug}`}
                className="group block"
              >
                <div className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-blue-300 h-full">
                  {/* Badge */}
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-semibold text-sm">
                        {BLOG_CATEGORIES[blog.category]?.icon} {BLOG_CATEGORIES[blog.category]?.label}
                      </span>
                      {index === 0 && (
                        <Award className="text-yellow-300" size={20} />
                      )}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {blog.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3 text-sm">
                      {blog.introduction.substring(0, 150)}...
                    </p>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                      <div className="flex items-center">
                        <Clock className="mr-1" size={14} />
                        <span>{blog.readTime}</span>
                      </div>
                      <div className="flex items-center">
                        <TrendingUp className="mr-1 text-green-500" size={14} />
                        <span>{blog.type === 'guide' ? 'Expert Guide' : 'Top 10 List'}</span>
                      </div>
                    </div>
                    
                    {/* CTA */}
                    <div className="flex items-center text-blue-600 font-semibold group-hover:text-blue-700 transition-colors">
                      <span className="text-sm">Read Article</span>
                      <ArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform" size={16} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Guides from the shared CMS (renders nothing until posts are published) */}
          <CmsGuideList />

          {/* Categories Section */}
          <div className="mt-16 pt-16 border-t border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Browse by Category
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(BLOG_CATEGORIES).map(([categoryId, category]) => (
                <div
                  key={categoryId}
                  className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200"
                >
                  <div className="text-4xl mb-3">{category.icon}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {category.label}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {category.description}
                  </p>
                  <div className="text-blue-600 text-sm font-semibold">
                    {getAllBlogPosts().filter(b => b.category === categoryId).length} Articles
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-10 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">
                Looking for Construction Cost Estimates?
              </h2>
              <p className="text-blue-100 mb-8 max-w-2xl mx-auto text-lg">
                Use our free AI-powered calculator to get instant cost estimates for your construction projects.
              </p>
              <Link
                to="/conceptual-estimate"
                className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg"
              >
                Get Free Estimate
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogIndex;

